import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Grid,
    Paper,
    Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import AcxButton from "components/UI/AcxButton";
import { observer } from "mobx-react";
import React from "react";
import { v4 as uuidv4 } from "uuid";
import { useStore } from "utils/useStore";
import MetadataConfiguration from "./MetadataConfiguration";
import {
    LOAD_CONFIG,
    OrganizationMetadataStore,
    SET_CONFIG,
} from "./Store/OrganizationMetadataStore";
import AcxToast from "components/UI/AcxToast";
import theme from "Theme/AppTheme";
import { metadataUIDeprecationMessage } from "constants/MetadataDeprecationMessages";

interface MetadataConfigurationWrapperProps {
    orgId: string;
}

type Props = MetadataConfigurationWrapperProps;

const MetadataConfigurationWrapper: React.FunctionComponent<
    React.PropsWithChildren<React.PropsWithChildren<Props>>
> = observer(({ orgId }) => {
    const metadataStore = useStore(OrganizationMetadataStore);
    const [isOpen, setIsOpen] = React.useState<boolean>(false);

    const toggleAccordion = () => {
        setIsOpen((prev) => !prev);
    };

    React.useEffect(() => {
        metadataStore.setOrgId(orgId);
    }, [metadataStore, orgId]);

    const isLoading =
        metadataStore.getTaskLoading(LOAD_CONFIG) ||
        metadataStore.getTaskLoading(SET_CONFIG);

    return (
        <Paper sx={{ marginTop: "1rem" }}>
            <Accordion square expanded={isOpen} onChange={toggleAccordion}>
                <AccordionSummary
                    aria-controls="panel1d-content"
                    id="panel1d-header"
                >
                    <Typography
                        sx={{
                            flexBasis: "60%",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        {isOpen ? (
                            <KeyboardArrowDownIcon />
                        ) : (
                            <KeyboardArrowRightIcon />
                        )}
                        Metadata Configuration
                    </Typography>
                </AccordionSummary>
                <AccordionDetails
                    sx={{
                        paddingLeft: theme.typography.pxToRem(32),
                        paddingRight: theme.typography.pxToRem(32),
                    }}
                >
                    <Grid
                        container
                        item
                        xs={12}
                        paddingBottom={theme.spacing(1)}
                    >
                        <AcxToast
                            title=""
                            message={metadataUIDeprecationMessage}
                            severity="errorV2"
                            fullWidth
                            fullWidthMessage
                            wrapText
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Grid item xs={12}>
                            {metadataStore.organizationMetadataConfiguration.map(
                                (config, index) => (
                                    <MetadataConfiguration
                                        key={uuidv4()}
                                        metadataStore={metadataStore}
                                        configIndex={index}
                                    />
                                ),
                            )}
                        </Grid>

                        <Grid item xs={12} container justifyContent="flex-end">
                            <AcxButton
                                onClick={() => metadataStore.handleFileAdd()}
                                color="primary"
                                sx={{ margin: "0.5rem 1rem 0.5rem 0.25rem" }}
                            >
                                Add File
                            </AcxButton>
                            <AcxButton
                                onClick={
                                    metadataStore.setOrganizationMetadataConfiguration
                                }
                                color="primary"
                                loading={isLoading}
                                disabled={
                                    isLoading ||
                                    metadataStore.organizationMetadataFields
                                        ?.length === 0
                                }
                                sx={{ margin: "0.5rem 0.25rem 0.5rem 0.25rem" }}
                            >
                                Save
                            </AcxButton>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </Paper>
    );
});

export default MetadataConfigurationWrapper;
